import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PORTFOLIO_CATEGORIES } from "../lib/categories";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star, Check, AlertCircle, Images, GripVertical } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import useLocalStorageState from "../hooks/useLocalStorageState";
import ConfirmDialog from "../components/ConfirmDialog";
import ImageCropper from "../components/ImageCropper";

const STATUS_OPTIONS = ["approved", "pending", "rejected"];
const COLUMNS = [
  { key: "preview", label: "Preview", required: true },
  { key: "title", label: "Title", required: true },
  { key: "category", label: "Category" },
  { key: "status", label: "Status" },
  { key: "featured", label: "Featured" },
  { key: "order", label: "Order" },
  { key: "created_at", label: "Created" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];
const statusBadge = {
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

const emptyForm = {
  title: "", category: "Restaurants", description: "",
  restaurant_id: "",
  before_image_id: "", after_image_id: "",
  gallery: [],
  order: 0, featured: false,
};

export default function AdminPortfolio() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [statusSel, setStatusSel] = useState(new Set());
  const [mineOnly, setMineOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    api.get("/restaurants").then((r) => setRestaurants(r.data || [])).catch(() => {});
  }, []);

  const [sortKey, setSortKey] = useState("order");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_portfolio_hidden_cols_v1", new Set(["updated_by_label", "updated_at"]));
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mineOnly) params.set("mine", "true");
      const r = await api.get(`/admin/portfolio?${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  }, [mineOnly]);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = (s) => {
    setPage(1);
    setStatusSel((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    if (statusSel.size === 0) return items;
    return items.filter((i) => statusSel.has(i.status));
  }, [items, statusSel]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    return arr;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  useEffect(() => { setPage(1); }, [pageSize, mineOnly, statusSel.size]);

  const openNew = () => { setEditing("new"); setForm({ ...emptyForm, order: items.length + 1 }); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "", category: p.category || "Restaurants", description: p.description || "",
      restaurant_id: p.restaurant_id || "",
      before_image_id: p.before_image_id || "", after_image_id: p.after_image_id || "",
      gallery: Array.isArray(p.gallery) ? p.gallery.map((g) => ({ image_id: g.image_id, title: g.title || "" })) : [],
      order: p.order, featured: !!p.featured,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, order: Number(form.order) || 0,
        restaurant_id: form.restaurant_id || null,
        before_image_id: form.before_image_id || null,
        after_image_id: form.after_image_id || null,
        gallery: (form.gallery || []).filter((g) => g.image_id).map((g) => ({ image_id: g.image_id, title: (g.title || "").trim() })),
      };
      if (editing === "new") { await api.post("/portfolio", payload); toast.success("Created"); }
      else { await api.put(`/portfolio/${editing}`, payload); toast.success("Updated"); }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = (p) => setDeleting(p);
  const confirmRemove = async () => {
    if (!deleting) return;
    try { await api.delete(`/portfolio/${deleting.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
    finally { setDeleting(null); }
  };

  const approve = async (p) => {
    try { await api.post(`/portfolio/${p.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/portfolio/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejecting(null); setRejectReason("");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-portfolio-page" className="p-6 lg:p-10 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Manage</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Portfolio</h1>
        </div>
        <button onClick={openNew} data-testid="add-portfolio-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-sm font-medium inline-flex items-center gap-2">
          <Plus size={16} /> Add Item
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252]">Status:</div>
        <MultiSelectStatusFilter options={STATUS_OPTIONS} selected={statusSel} onToggle={toggleStatus} testIdPrefix="pf-filter" />
        {!isAdmin && (
          <label className="flex items-center gap-2 text-sm ml-2 cursor-pointer">
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} data-testid="pf-mine-only" />
            My drafts only
          </label>
        )}
        {statusSel.size > 0 && (
          <button data-testid="pf-clear-filter" onClick={() => setStatusSel(new Set())} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear filter</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="pf-cols"/></div>
      </div>

      {loading ? (
        <div className="text-[#525252]">Loading...</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center" data-testid="portfolio-empty-admin">
          <p className="text-[#525252]">No portfolio items match the current filter.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="portfolio-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("preview") && <th className="px-4 py-3 text-left w-20">Preview</th>}
                  {showCol("title") && <th className="px-4 py-3 text-left"><SortableHeader label="Title" sortKey="title" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("category") && <th className="px-4 py-3 text-left"><SortableHeader label="Category" sortKey="category" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("status") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="status" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("featured") && <th className="px-4 py-3 text-left">Featured</th>}
                  {showCol("order") && <th className="px-4 py-3 text-left"><SortableHeader label="Order" sortKey="order" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Created" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => (
                  <tr key={p.id} data-testid={`portfolio-row-${p.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("preview") && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <div className="w-10 h-10 bg-[#F0F0F0] overflow-hidden rounded-sm">
                            {p.before_image_id && <img src={fileUrl(p.before_image_id)} alt="" className="w-full h-full object-cover"/>}
                          </div>
                          <div className="w-10 h-10 bg-[#F0F0F0] overflow-hidden rounded-sm">
                            {p.after_image_id && <img src={fileUrl(p.after_image_id)} alt="" className="w-full h-full object-cover"/>}
                          </div>
                        </div>
                      </td>
                    )}
                    {showCol("title") && (
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0A0A0A] truncate max-w-[280px]" title={p.title}>{p.title}</div>
                        {p.status === "rejected" && p.rejection_reason && (
                          <div className="mt-1 text-[11px] text-rose-700 inline-flex items-center gap-1">
                            <AlertCircle size={11} /> {p.rejection_reason}
                          </div>
                        )}
                      </td>
                    )}
                    {showCol("category") && <td className="px-4 py-3 text-[#525252]">{p.category}</td>}
                    {showCol("status") && <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${statusBadge[p.status] || ""}`}>{p.status}</span></td>}
                    {showCol("featured") && <td className="px-4 py-3">{p.featured && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[var(--brand-primary)] text-white px-1.5 py-0.5 rounded-sm"><Star size={10}/> Featured</span>}</td>}
                    {showCol("order") && <td className="px-4 py-3 text-[#525252] font-mono text-xs">{String(p.order).padStart(2, "0")}</td>}
                    {showCol("created_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-[#525252] text-xs truncate max-w-[200px]" title={p.updated_by_label}>{p.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {isAdmin && p.status !== "approved" && (
                          <button onClick={() => approve(p)} data-testid={`approve-portfolio-${p.id}`} title="Approve" className="p-2 hover:bg-emerald-50 text-emerald-700 rounded-sm"><Check size={16}/></button>
                        )}
                        {isAdmin && p.status === "pending" && (
                          <button onClick={() => setRejecting(p)} data-testid={`reject-portfolio-${p.id}`} title="Reject" className="p-2 hover:bg-rose-50 text-rose-700 rounded-sm"><AlertCircle size={16}/></button>
                        )}
                        <button onClick={() => openEdit(p)} data-testid={`edit-portfolio-${p.id}`} title="Edit" className="p-2 hover:bg-black/5 rounded-sm"><Pencil size={16}/></button>
                        {isAdmin && (
                          <button onClick={() => remove(p)} data-testid={`delete-portfolio-${p.id}`} title="Delete" className="p-2 hover:bg-[var(--brand-primary)]/5 text-[var(--brand-primary)] rounded-sm"><Trash2 size={16}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="portfolio-editor-modal">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="w-full max-w-lg bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">{editing === "new" ? "New Item" : "Edit Item"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <div className="space-y-4">
              <FInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="portfolio-title" required/>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select data-testid="input-portfolio-category" value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })} required className={inputCls}>
                  {PORTFOLIO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <FArea label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="portfolio-description"/>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Linked restaurant <span className="text-[#525252] font-normal">(optional)</span></label>
                  {form.restaurant_id && (
                    <button
                      type="button"
                      data-testid="portfolio-prefill-from-restaurant"
                      onClick={() => {
                        const r = restaurants.find((x) => x.id === form.restaurant_id);
                        if (!r) return;
                        setForm((f) => ({
                          ...f,
                          title: f.title?.trim() ? f.title : `${r.name} — Project`,
                        }));
                        toast.success("Title pre-filled from restaurant");
                      }}
                      className="text-[10px] uppercase tracking-wider text-[var(--brand-primary)] hover:underline"
                    >
                      Use restaurant name
                    </button>
                  )}
                </div>
                <select
                  value={form.restaurant_id || ""}
                  data-testid="portfolio-restaurant"
                  onChange={(e) => {
                    const newId = e.target.value;
                    const r = restaurants.find((x) => x.id === newId);
                    setForm((f) => ({
                      ...f,
                      restaurant_id: newId,
                      // Auto-suggest title only when empty (never overwrite a user-typed title)
                      title: f.title?.trim() ? f.title : (r ? `${r.name} — Project` : f.title),
                    }));
                  }}
                  className={inputCls}
                >
                  <option value="">— None —</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.is_active ? "" : " (inactive)"}</option>
                  ))}
                </select>
                <p className="text-[10px] text-[#525252] mt-1">Linking surfaces this portfolio in the restaurant's analytics drawer.</p>
              </div>

              <FInput label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testId="portfolio-order"/>
              <ImageCropper
                label="Before image"
                fileId={form.before_image_id}
                onUpload={(id) => setForm({ ...form, before_image_id: id })}
                onClear={() => setForm({ ...form, before_image_id: "" })}
                testId="before"
              />
              <ImageCropper
                label="After image"
                fileId={form.after_image_id}
                onUpload={(id) => setForm({ ...form, after_image_id: id })}
                onClear={() => setForm({ ...form, after_image_id: "" })}
                testId="after"
              />

              <GalleryEditor
                items={form.gallery}
                onChange={(arr) => setForm({ ...form, gallery: arr })}
              />
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-black/10 rounded-sm hover:bg-[#FAFAFA] transition-colors">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="input-portfolio-featured" className="mt-1"/>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5"><Star size={14} className="text-[var(--brand-primary)]"/> Mark as Featured</div>
                  <div className="text-xs text-[#525252] mt-0.5">Featured items appear on the Home page (max 3 shown).</div>
                </div>
              </label>
            </div>
            <button type="submit" disabled={saving} data-testid="save-portfolio-button"
                    className="mt-8 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60">
              {saving ? "Saving..." : "Save"}
            </button>
          </form>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-50 flex" data-testid="reject-portfolio-modal">
          <div className="flex-1 bg-black/40" onClick={() => { setRejecting(null); setRejectReason(""); }}/>
          <form onSubmit={submitReject} className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">Reject Portfolio Item</h2>
              <button type="button" onClick={() => { setRejecting(null); setRejectReason(""); }} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <p className="text-sm text-[#525252] mb-4"><strong>{rejecting.title}</strong> will be marked as rejected.</p>
            <textarea data-testid="reject-reason" rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason..." className={inputCls}/>
            <button type="submit" data-testid="submit-reject" className="mt-6 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium">Reject</button>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete this portfolio item?"
        description={deleting ? `“${deleting.title}” will be permanently removed along with its images. This can't be undone.` : ""}
        confirmText="Delete"
        destructive
        onConfirm={confirmRemove}
        testId="delete-portfolio-dialog"
      />
    </div>
  );
}

function GalleryEditor({ items = [], onChange }) {
  const add = (image_id) => onChange([...items, { image_id, title: "" }]);
  const setTitle = (idx, title) => onChange(items.map((g, i) => i === idx ? { ...g, title } : g));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx) => {
    if (idx === items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };
  return (
    <div data-testid="gallery-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium inline-flex items-center gap-1.5">
          <Images size={14}/> Project gallery <span className="text-[#525252] font-normal text-xs">({items.length})</span>
        </label>
      </div>
      <p className="text-[10px] text-[#525252] mb-2">Add multiple images with context titles. Shown on the public detail page.</p>

      <div className="space-y-3">
        {items.map((g, idx) => (
          <div key={`${g.image_id}-${idx}`} data-testid={`gallery-row-${idx}`} className="border border-black/10 rounded-sm p-3 bg-[#FAFAFA] flex gap-3">
            <div className="flex flex-col gap-1 text-[#525252]">
              <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0} title="Move up"
                      className="text-[10px] px-1 disabled:opacity-30">▲</button>
              <GripVertical size={12} className="opacity-30 self-center"/>
              <button type="button" onClick={() => moveDown(idx)} disabled={idx === items.length - 1} title="Move down"
                      className="text-[10px] px-1 disabled:opacity-30">▼</button>
            </div>
            <img src={fileUrl(g.image_id)} alt="" loading="lazy"
                 className="w-20 h-16 object-cover rounded-sm bg-white border border-black/10"/>
            <div className="flex-1 flex flex-col gap-2">
              <input
                type="text" value={g.title || ""} maxLength={120}
                placeholder="Caption / context (optional)"
                data-testid={`gallery-title-${idx}`}
                onChange={(e) => setTitle(idx, e.target.value)}
                className="w-full bg-white border border-black/10 rounded-sm px-3 py-1.5 text-sm focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"/>
              <button type="button" onClick={() => remove(idx)}
                      data-testid={`gallery-remove-${idx}`}
                      className="self-start text-xs text-[var(--brand-primary)] hover:underline">Remove</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <ImageCropper
          label="Add image to gallery"
          fileId={""}
          onUpload={(id) => add(id)}
          onClear={() => {}}
          testId="gallery-add"
        />
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";
function FInput({ label, value, onChange, testId, type = "text", required }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <input data-testid={`input-${testId}`} type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls}/>
    </div>
  );
}
function FArea({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <textarea data-testid={`input-${testId}`} value={value ?? ""} rows={3} onChange={(e) => onChange(e.target.value)} className={inputCls}/>
    </div>
  );
}
